//@flow
import * as React from 'react';
import i18n from 'app/common/lib/i18n';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { REASON_KEYS } from 'app/common/react/components/undiscovery/FlagPin/SimplifiedReportPin/ReportReasons';
import { Box, Divider, Heading, Text, Link, Switch } from 'gestalt';

type Props = {|
  onChangeBlocked: (id: string, name: string) => () => void,
  reason: $Values<REASON_KEYS>,
  user: ProfileUser,
  userBlocked: boolean,
|};

export default function PolicyViolationConfirmation({
  reason,
  user,
  userBlocked,
  onChangeBlocked,
}: Props) {
  const profileConfirmations = {
    [REASON_KEYS.SPAM]: {
      title: i18n._('Report spam?', 'Confirm report profile reason title'),
      removals: [
        i18n._('Misleading content or behavior', 'Example of spam'),
        i18n._('Repetitive posts', 'Example of spam'),
        i18n._('Unsolicited commercial messages', 'Example of spam'),
      ],
    },
    [REASON_KEYS.PORN]: {
      title: i18n._('Report nudity or pornography?', 'Confirm report profile reason title'),
      removals: [
        i18n._('Sexualized nudity', 'Example of porn'),
        i18n._('Depictions of sexual acts', 'Example of porn'),
        i18n._('Fetish content', 'Example of porn'),
      ],
    },
    [REASON_KEYS.SELF_INJURY]: {
      title: i18n._('Report self-harm?', 'Confirm report profile reason title'),
      removals: [
        i18n._(
          'Encouraging self-mutilation, eating disorders and drug abuse',
          'Example of self-injury',
        ),
        i18n._('Graphic depictions of self-harm', 'Example of self-injury'),
      ],
    },
    [REASON_KEYS.MEDICAL_MISINFORMATION]: {
      title: i18n._('Report medical misinformation?', 'Confirm report profile reason title'),
      removals: [
        i18n._(
          'False cures for cancer or other chronic or terminal illnesses',
          'Example of medical misinformation',
        ),
        i18n._('Anti-vaccination advice', 'Example of medical misinformation'),
      ],
      nonRemovals: [
        i18n._(
          'Diet advice, including cleanses and supplements that can boost the immune system',
          'Example of a profile we will not remove',
        ),
        i18n._(
          'Teas, oils and herbs proposed for aromatic and sleep quality',
          'Example of a profile we will not remove',
        ),
      ],
    },
    [REASON_KEYS.HATE_SPEECH]: {
      title: i18n._('Report hateful speech?', 'Confirm report profile reason title'),
      removals: [
        i18n._('Attacks or slurs directed at protected groups', 'Example of hate speech'),
        i18n._('People or groups that advocate hate speech', 'Example of hate speech'),
      ],
      nonRemovals: [
        i18n._('Profanity', 'Example of a profile we will not remove'),
        i18n._('Attacks on public figures', 'Example of a profile we will not remove'),
        i18n._(
          'Criticism of government, political or religious leaders',
          'Example of a profile we will not remove',
        ),
      ],
    },
    [REASON_KEYS.HARASSMENT]: {
      title: i18n._('Report harassment?', 'Confirm report profile reason title'),
      removals: [
        i18n._('Attacks directed at a specific private individual', 'Example of harassment'),
        i18n._('Personally identifiable information', 'Example of harassment'),
      ],
      nonRemovals: [
        i18n._('Attacks on public figures', 'Example of a profile we will not remove'),
        i18n._(
          'Criticism of government, political or religious leaders',
          'Example of a profile we will not remove',
        ),
      ],
    },
    [REASON_KEYS.VIOLENCE]: {
      title: i18n._('Report graphic violence?', 'Confirm report profile reason title'),
      removals: [
        i18n._('Extreme graphic violence', 'Example of violence'),
        i18n._('Glorification or promotion of violence', 'Example of violence'),
        i18n._('Threats of physical harm', 'Example of violence'),
      ],
      nonRemovals: [
        i18n._(
          'Historical, educational or newsworthy content',
          'Example of a profile we will not remove',
        ),
        i18n._(
          'Political protests, advocacy or other social causes',
          'Example of a profile we will not remove',
        ),
      ],
    },
  };

  const { full_name, username, id } = user;
  const displayName = full_name ? `${full_name} (${username})` : username;
  const blockUserSwitch = (
    <Box paddingX={2} paddingY={2}>
      <Box alignItems="center" display="flex" direction="row" paddingX={2}>
        <Box paddingX={2}>
          <Heading size="xs">
            {interpolateNamedTemplate(i18n._('Block {{ name }}', 'Block a user heading'), {
              name: full_name || user.username,
            })}
          </Heading>
          <Box marginTop={2} marginRight={2}>
            <Text>
              {interpolateNamedTemplate(
                i18n._(
                  "If you block {{ names }}, both of you can still view each other's profiles, but you can't message each other, follow or save each other's Pins.",
                  'Explanation of blocking a user',
                ),
                {
                  names: (
                    <Link href={`/${username}`} inline key="blockUserLink" target="blank">
                      {displayName}
                    </Link>
                  ),
                },
              )}
            </Text>
          </Box>
        </Box>
        <Box paddingX={2}>
          <Switch
            id="blockUser"
            onChange={onChangeBlocked(id, displayName)}
            switched={userBlocked}
          />
        </Box>
      </Box>
    </Box>
  );

  const details = profileConfirmations[reason];
  const { removals, nonRemovals } = details;

  return (
    <React.Fragment>
      <Box maxHeight={'60vh'} overflow="auto">
        <Box paddingX={6} data-test-id="detail-text">
          <Box paddingY={4}>
            <Heading size="sm">{details.title}</Heading>
          </Box>
          <Box paddingY={2}>
            <React.Fragment>
              {removals.map((item, idx) => (
                <Text key={`removals-${idx}`}>{'• ' + item}</Text>
              ))}
            </React.Fragment>
          </Box>
          {nonRemovals && (
            <Box>
              <Text weight="bold">
                {i18n._(
                  'We don’t remove things like',
                  'Title for list of content we do not remove',
                )}
              </Text>
              <Box paddingY={2}>
                <React.Fragment>
                  {nonRemovals.map((item, idx) => (
                    <Text key={`nonRemovals-${idx}`}>{'• ' + item}</Text>
                  ))}
                </React.Fragment>
              </Box>
            </Box>
          )}
          {details.notes && (
            <Text>
              {interpolateNamedTemplate(i18n._('Note: {{ notes }}', 'Note to the user'), {
                notes: details.notes,
              })}
            </Text>
          )}
        </Box>
        <Box paddingY={2}>
          <Divider />
        </Box>
        {blockUserSwitch}
      </Box>
    </React.Fragment>
  );
}
